import { useMemo } from 'react'
import dayjs from 'dayjs'

import UserOutlinedIcon from '@ant-design/icons/UserOutlined'

import usePatient from '@shared/hooks/src/usePatient'
import { addressToString } from '@shared/utils'

import { BreakGlassPaper } from '@components/BreakGlass'
import Property, { CBOProperty, PatientProperty, PharmacyProperty, ProviderProperty } from '@components/Details/Property'
import Section from '@components/Details/Section'

export default function PatientSection({ data, mini = false }) {
  const { data: user, isPending } = usePatient(data.user.id, {
    enabled: Boolean(data.user.id),
  })

  const dob = useMemo(() => {
    if (!user?.dob) return '—'
    const age = dayjs().diff(dayjs(user.dob), 'years')
    return `${dayjs(user.dob).format('L')} ${age}yo`
  }, [user])

  const address = useMemo(() => {
    if (!user?.homeAddress) return '—'
    return addressToString(user.homeAddress)
  }, [user])

  if (isPending) return <Loading mini={mini} />

  return (
    <BreakGlassPaper patient={user}>
      <Section paper mini={mini} title="Patient" icon={mini ? null : <UserOutlinedIcon />}>
        <PatientProperty label="Name" user={user} />
        <Property label="DOB">{dob}</Property>
        <Property label="Born As">{user.bornAs || '—'}</Property>
        <Property label="Address" sx={{ value: { whiteSpace: 'pre-wrap' } }}>
          {address}
        </Property>
        <CBOProperty label="CBO" data={user.cbo} />
        <ProviderProperty label="Provider" user={user.provider} />
        <PharmacyProperty label="Pharmacy" data={user.pharmacy} />
      </Section>
    </BreakGlassPaper>
  )
}

const Loading = ({ mini = false }) => (
  <Section paper mini={mini} title="Patient" icon={mini ? null : <UserOutlinedIcon />}>
    <PatientProperty.Loading label="Name" />
    <Property.Loading label="DOB" />
    <Property.Loading label="Born As" />
    <CBOProperty.Loading label="CBO" />
    <ProviderProperty.Loading label="Provider" />
    <PharmacyProperty.Loading label="Pharmacy" />
  </Section>
)

PatientSection.Loading = Loading
